'use client';
import React, { useEffect, useState } from 'react';
import { useHeader } from '@/components/complex/header';
import { useBottomNavigation } from '@/components/complex/bottom-navigation';
import { useRouter } from 'next/navigation';
import Button from '@/components/primitives/button';
import SkeletonFeedItem from '@/components/complex/skeleton-feed-item';

import FeedItem from '@/components/complex/feed-item';
import { useFeedData } from './hooks/useFeedData';
import Typography from '@/components/primitives/typography';
import CommentActionSheet from '@/screen/feed/view/components/comment-action-sheet';
import styles from './styles.module.scss';
import ErrorText from '@/components/common/error-text';
import api from '@/api';
import { FeedDto } from '@/api/feed';
import CommentPanel from '@/screen/feed/view/components/comment-panel';
import FeedMasonry, { FeedMasonryItem } from '@/components/complex/feed-masonry';
import useMediaQuery from '@/libs/hooks/use-media-query';
import { PC_MEDIA_QUERY } from '@/libs/config/responsive';

interface FeedViewScreenProps {
  feedId: string;
  // 서버에서 미리 가져온 피드. 첫 렌더(SSR)부터 본문을 그리는 데 사용한다.
  initialFeed?: FeedDto | null;
}

/**
 * Feed view screen component
 */
const FeedViewScreen = ({ feedId, initialFeed }: FeedViewScreenProps) => {
  const router = useRouter();

  // Set up header
  useHeader({
    title: '피드',
    desktopTitle: '피드 상세',
    useBack: true,
  });

  // PC는 댓글을 시트 대신 본문 옆(1024px 미만은 아래) 패널로 보여 준다.
  const isPc = useMediaQuery(PC_MEDIA_QUERY);

  // Enable bottom navigation
  useBottomNavigation(true);

  // Use custom hooks
  const { 
    feed, 
    isLoading, 
    error, 
  } = useFeedData(feedId, initialFeed);

  // State for comment sheet
  const [isCommentSheetOpen, setIsCommentSheetOpen] = React.useState(false);

  // 연관 피드
  const [relatedFeeds, setRelatedFeeds] = useState<FeedDto[]>([]);
  useEffect(() => {
    if (!feedId) return;
    api.feed.related(feedId, { size: 10 })
      .then(res => setRelatedFeeds(res.list ?? []))
      .catch(() => {});
  }, [feedId]);

  // Handle edit button click
  const handleEdit = () => {
    if (feedId) {
      router.push(`/feeds/${feedId}/edit`);
    }
  };

  // Handle delete
  const handleDelete = () => {
    // Navigate back to the previous page after feed deletion
    router.back();
  };

  // Loading state
  if (isLoading) {
    return <SkeletonFeedItem />;
  }

  // Error state
  if (error) {
    return (
      <div className={styles.centeredContainerWithPadding}>
        <ErrorText>{error}</ErrorText>
        <Button onClick={() => router.back()}>
          뒤로 가기
        </Button>
      </div>
    );
  }

  // No feeds found
  if (!feed) {
    return (
      <div className={styles.centeredContainerWithMorePadding}>
        <Typography className={styles.notFoundText}>피드를 찾을 수 없습니다.</Typography>
        <Button onClick={() => router.back()}>
          뒤로 가기
        </Button>
      </div>
    );
  }

  const handleComment = () => {
    if (isPc) {
      const panel = document.getElementById('comments');
      panel?.scrollIntoView({ block: 'nearest' });
      panel?.querySelector<HTMLElement>('textarea, input')?.focus({ preventScroll: true });
      return;
    }
    setIsCommentSheetOpen(true);
  };

  return (
    <div className={styles.container}>
      <div className={styles.layout}>
        {/* Feed content with integrated action buttons */}
        <div className={styles.main}>
          <FeedItem
            {...feed}
            isFirst
            disableNavigate
            variant="detail"
            onComment={handleComment}
            onShare={() => {}}
            onDelete={handleDelete}
          />
        </div>

        {isPc && <CommentPanel feedId={feedId} commentCount={feed.commentCount} className={styles.comments} />}
      </div>

      {/* 연관 피드. PC는 목록과 같은 masonry로 본문 아래에 둔다. */}
      {relatedFeeds.length > 0 && (
        <section className={styles.relatedSection} aria-labelledby="related-feeds-title">
          <div className={styles.relatedHeader}>
            <h2 id="related-feeds-title" className={styles.relatedTitle}>이 피드와 연관된 다른 피드</h2>
          </div>
          <FeedMasonry>
            {relatedFeeds.map(item => (
              <FeedMasonryItem key={item.sid} feedId={item.sid}>
                <FeedItem {...item} />
              </FeedMasonryItem>
            ))}
          </FeedMasonry>
        </section>
      )}

      {/* Comments ActionSheet */}
      {!isPc && (
        <CommentActionSheet
          isOpen={isCommentSheetOpen}
          onClose={() => setIsCommentSheetOpen(false)}
          feedId={feedId}
        />
      )}
    </div>
  );
};

export default FeedViewScreen;
